// components/steps/StepBudget.jsx
import React, { useState, useEffect, useRef } from 'react';

const StepBudget = ({ data, updateData, skip, goBack }) => {
  const [currencies, setCurrencies] = useState([]);
  const [currency, setCurrency] = useState(data.currency || 'USD');
  const [searchTerm, setSearchTerm] = useState('');
  const [amount, setAmount] = useState(
    data.budget?.amount
      ? Number(data.budget.amount).toLocaleString()
      : ''
  );
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    let active = true;
    fetch('https://restcountries.com/v3.1/all?fields=currencies')
      .then((res) => res.json())
      .then((countries) => {
        if (!active) return;
        const map = new Map();
        countries.forEach((country) => {
          if (country.currencies) {
            Object.entries(country.currencies).forEach(([code, info]) => {
              if (!map.has(code)) {
                map.set(code, {
                  code,
                  description: info.name,
                  symbol: info.symbol || code,
                });
              }
            });
          }
        });
        setCurrencies(
          Array.from(map.values()).sort((a, b) =>
            a.code.localeCompare(b.code)
          )
        );
      })
      .catch((err) => console.error('Failed to fetch currencies', err))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () =>
      document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = currencies.filter(
    (c) =>
      c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectCurrency = (c) => {
    setCurrency(c.code);
    setSearchTerm(c.code);
    setOpen(false);
  };

  const handleAmountChange = (e) => {
    const raw = e.target.value
      .replace(/,/g, '')
      .replace(/[^0-9]/g, '');
    const formatted = raw.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      ','
    );
    setAmount(formatted);
  };

  const isNextDisabled =
    loading ||
    !amount.toString().replace(/,/g, '').trim() ||
    !currency;

  const handleNext = () => {
    if (isNextDisabled) return;
    // **ONE** updateData call for both fields:
    updateData('budget', {
      amount: amount.replace(/,/g, ''),
      currency
    });
  };

  return (
    <div className="bg-white/60 backdrop-blur p-6 rounded-xl shadow-lg max-w-md mx-auto">
      <h2 className="text-xl font-semibold mb-4 text-center">
        What is your budget?
      </h2>

      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
        <div
          className="relative w-full md:w-1/3"
          ref={dropdownRef}
        >
          <div
            className="w-full p-3 border rounded-lg flex justify-between items-center cursor-pointer"
            onClick={() => setOpen((o) => !o)}
          >
            <span>{currency || 'Select currency'}</span>
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
          {open && (
            <div className="absolute z-20 bg-white w-full border rounded-lg mt-1 max-h-60 overflow-auto">
              <input
                type="text"
                className="w-full p-2 border-b"
                placeholder="Search currency..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
              {filtered.map((c, idx) => (
                <div
                  key={idx}
                  onClick={() => selectCurrency(c)}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex justify-between"
                >
                  <span>
                    {c.symbol} {c.code}
                  </span>
                  <span className="text-xs text-gray-500">
                    {c.description}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <input
          type="text"
          className="w-full md:w-2/3 p-3 border rounded-lg"
          value={amount}
          onChange={handleAmountChange}
          placeholder="Enter amount"
        />
      </div>

      <div className="flex justify-between items-center mt-6">
        {goBack && (
          <button
            onClick={goBack}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
          >
            Back
          </button>
        )}
        <div className="flex space-x-4">
          <button
            onClick={handleNext}
            disabled={isNextDisabled}
            className={`px-6 py-2 rounded-lg transition ${
              isNextDisabled
                ? 'bg-blue-300 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Next
          </button>
          <button
            onClick={skip}
            className="px-6 py-2 bg-transparent border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepBudget;
